import { webPaths } from "@zplanem/web-paths"
import { useEffect, useMemo, useState } from "react"
import { CreateTicketModal } from "../../components/tickets/create-ticket-modal"
import { TicketBoard } from "../../components/tickets/ticket-board"
import { TicketFilterBar } from "../../components/tickets/ticket-filter-bar"
import {
	clearMissingTicketFilters,
	type TicketBoardFilters,
	topicsVisibleForTicketScope,
} from "../../components/tickets/ticket-filter-model"
import { isTicketManager } from "../../components/tickets/ticket-form-model"
import { TicketList } from "../../components/tickets/ticket-list"
import {
	TicketAddButton,
	TicketViewSwitcher,
} from "../../components/tickets/ticket-view-controls"
import { useIsDesktop } from "../../hooks/use-is-desktop"
import { usePropertyMemberships } from "../../hooks/use-property-memberships"
import { useTasksViewMode } from "../../hooks/use-tasks-view-mode"
import { useTopics } from "../../hooks/use-topics"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"
import { useUser } from "../../user-context"
import { useProjectDetailContext } from "./project-detail-context"

export function ProjectTasksTab() {
	const { propertyId, project } = useProjectDetailContext()
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const { profile } = useUser()
	const memberships = usePropertyMemberships(propertyId)
	const topics = useTopics(propertyId)
	const [createOpen, setCreateOpen] = useState(false)
	const [filters, setFilters] = useState<TicketBoardFilters>(() => ({
		projectId: project.id,
	}))
	const isDesktop = useIsDesktop()
	const { viewMode, setViewMode } = useTasksViewMode(`project:${project.id}`)

	const visibleTopics = useMemo(
		() => topicsVisibleForTicketScope(topics.data ?? [], project.id),
		[topics.data, project.id],
	)
	const filterOptionIds = useMemo(
		() => ({
			projectIds: new Set([project.id]),
			assigneeIds: new Set([
				...(memberships.data?.owner ? [memberships.data.owner.id] : []),
				...(memberships.data?.memberships.map(
					(membership) => membership.user.id,
				) ?? []),
			]),
			topicIds: new Set(visibleTopics.map((topic) => topic.id)),
		}),
		[project.id, memberships.data, visibleTopics],
	)

	useEffect(() => {
		setFilters((current) => {
			const next = clearMissingTicketFilters(
				{ ...current, projectId: project.id },
				filterOptionIds,
			)
			const locked = { ...next, projectId: project.id }
			if (
				locked.projectId === current.projectId &&
				locked.assigneeId === current.assigneeId &&
				locked.topicId === current.topicId &&
				locked.kind === current.kind
			) {
				return current
			}
			return locked
		})
	}, [project.id, filterOptionIds])

	const canManageTickets = isTicketManager(memberships.data, profile?.id)
	const effectiveMode = isDesktop ? viewMode : "list"

	return (
		<div>
			<div className="mb-4 flex flex-wrap items-center justify-between gap-2">
				<TicketViewSwitcher
					value={viewMode}
					onChange={setViewMode}
					t={t.tickets}
				/>
				{canManageTickets ? (
					<TicketAddButton onClick={() => setCreateOpen(true)} t={t.tickets} />
				) : null}
			</div>
			<TicketFilterBar
				propertyId={propertyId}
				filters={filters}
				projects={[project]}
				memberships={memberships.data}
				topics={visibleTopics}
				canManageTopics={canManageTickets}
				t={t.tickets}
				onChange={(next) => setFilters({ ...next, projectId: project.id })}
				lockedProjectId={project.id}
			/>
			{effectiveMode === "kanban" ? (
				<TicketBoard
					propertyId={propertyId}
					filters={filters}
					topics={visibleTopics}
					projects={[project]}
					showProjectLabels={false}
					lockedProjectId={project.id}
					returnTo={{
						path: webPaths.projectTasks(propertyId, project.id),
						label: t.tickets.backToProjectTasks,
					}}
				/>
			) : (
				<TicketList
					propertyId={propertyId}
					filters={filters}
					topics={visibleTopics}
					projects={[project]}
					showProjectLabels={false}
					lockedProjectId={project.id}
					returnTo={{
						path: webPaths.projectTasks(propertyId, project.id),
						label: t.tickets.backToProjectTasks,
					}}
				/>
			)}
			{createOpen && canManageTickets ? (
				<CreateTicketModal
					propertyId={propertyId}
					initialFilters={filters}
					onClose={() => setCreateOpen(false)}
				/>
			) : null}
		</div>
	)
}
